/**
 * Previsao de datas por DURACAO e ANTECESSOR (pedido 2026-08-31).
 *
 * O item pode ter so a quantidade de dias, sem data nenhuma. Nesse caso as
 * datas saem da cadeia, como num cronograma de projeto:
 *
 *  - o ANTECESSOR e, por padrao, o irmao anterior na ordem; da para apontar
 *    outro item explicitamente (`predecessorId`);
 *  - inicio = fim do antecessor (o mesmo dia em que ele termina);
 *  - fim = inicio + duracao;
 *  - o PRIMEIRO da cadeia nao tem antecessor: ancora no inicio do PAI;
 *  - o pai mostra o inicio dele e o fim do ultimo filho.
 *
 * Data posta a mao VENCE: item com inicio e fim proprios ignora duracao e
 * antecessor, e a duracao dele passa a ser o intervalo entre as duas datas.
 *
 * Sao DIAS CORRIDOS — e o que o cronograma ja conta hoje. Fica tudo aqui,
 * puro, para o servidor e a UI usarem a mesma conta.
 */

const DIA_MS = 86_400_000

/** Item como a regra precisa ver. */
export type ItemDoCronograma = {
  id: string
  parentWorkItemId: string | null
  /** Ordem dentro do pai — define quem e o antecessor padrao. */
  sortOrder: number
  /** Datas postas a mao (ISO) — quando existem, mandam. */
  startAt: string | null
  dueAt: string | null
  /** Quantidade de dias; null = sem duracao declarada. */
  durationDays: number | null
  /** Antecessor escolhido a mao; null = o irmao anterior na ordem. */
  predecessorId: string | null
}

/** Datas resolvidas de um item. */
export type DatasResolvidas = {
  startAt: string | null
  dueAt: string | null
  /**
   * De onde saiu o FIM — que e o que a tela precisa explicar: `manual` (o
   * usuario pos a data), `calculado` (inicio + duracao), `filhos` (pai que
   * herda o fim do ultimo filho) ou `vazio` (nao deu para prever).
   */
  origem: 'manual' | 'calculado' | 'filhos' | 'vazio'
  /** Dias entre inicio e fim — util para mostrar no campo de duracao. */
  durationDays: number | null
}

const dia = (iso: string) => Math.floor(new Date(iso).getTime() / DIA_MS)
const doDia = (d: number) => new Date(d * DIA_MS).toISOString()

/** Dias corridos entre duas datas (mesmo dia = 0). */
export function diasEntre(inicio: string, fim: string): number {
  return Math.max(0, dia(fim) - dia(inicio))
}

/** Fim de um trecho que comeca em `inicio` e dura `dias`. */
export function fimPorDuracao(inicio: string, dias: number): string {
  return doDia(dia(inicio) + Math.max(0, Math.round(dias)))
}

/**
 * Resolve as datas de uma arvore inteira.
 *
 * Devolve um mapa id -> datas. A conta desce a arvore em ordem: cada filho
 * depende do antecessor, e o pai so fecha depois dos filhos.
 */
export function resolverCronograma(itens: ItemDoCronograma[]): Map<string, DatasResolvidas> {
  const porId = new Map(itens.map((i) => [i.id, i]))
  const filhosDe = new Map<string | null, ItemDoCronograma[]>()
  for (const it of itens) {
    const chave = it.parentWorkItemId && porId.has(it.parentWorkItemId) ? it.parentWorkItemId : null
    const lista = filhosDe.get(chave) ?? []
    lista.push(it)
    filhosDe.set(chave, lista)
  }
  for (const lista of filhosDe.values()) lista.sort((a, b) => a.sortOrder - b.sortOrder)

  /** Antecessor efetivo: o escolhido a mao, ou o irmao anterior na ordem. */
  const antecessorDe = (it: ItemDoCronograma): ItemDoCronograma | null => {
    if (it.predecessorId) return porId.get(it.predecessorId) ?? null
    const irmaos = filhosDe.get(it.parentWorkItemId && porId.has(it.parentWorkItemId) ? it.parentWorkItemId : null) ?? []
    const i = irmaos.findIndex((x) => x.id === it.id)
    return i > 0 ? irmaos[i - 1]! : null
  }

  /**
   * Inicio e fim sao duas contas separadas de proposito.
   *
   * O inicio do filho olha para CIMA (antecessor e pai) e o fim do pai olha
   * para BAIXO (os filhos). Se fossem uma conta so, pai e filho ficariam se
   * esperando.
   */
  const inicios = new Map<string, string | null>()
  const fins = new Map<string, string | null>()
  const visitando = new Set<string>()

  const inicioDe = (it: ItemDoCronograma): string | null => {
    if (inicios.has(it.id)) return inicios.get(it.id)!
    if (visitando.has(it.id)) return it.startAt // antecessor circular: para aqui
    visitando.add(it.id)
    let v = it.startAt
    if (!v) {
      const ant = antecessorDe(it)
      const pai = it.parentWorkItemId ? porId.get(it.parentWorkItemId) : undefined
      v = ant ? fimDe(ant) : pai ? inicioDe(pai) : null
    }
    visitando.delete(it.id)
    inicios.set(it.id, v)
    return v
  }

  const fimDe = (it: ItemDoCronograma): string | null => {
    if (fins.has(it.id)) return fins.get(it.id)!
    if (visitando.has(it.id) && !it.dueAt) return it.dueAt
    fins.set(it.id, it.dueAt) // corta ciclo enquanto desce
    const inicio = inicioDe(it)
    const porDuracao = inicio && it.durationDays != null ? fimPorDuracao(inicio, it.durationDays) : null
    const dosFilhos = (filhosDe.get(it.id) ?? []).reduce<string | null>((mai, f) => {
      const fim = fimDe(f)
      return fim && (!mai || dia(fim) > dia(mai)) ? fim : mai
    }, null)
    const v = it.dueAt ?? porDuracao ?? dosFilhos
    fins.set(it.id, v)
    return v
  }

  const saida = new Map<string, DatasResolvidas>()
  for (const it of itens) {
    let inicio = inicioDe(it)
    const fim = fimDe(it)
    // pai sem inicio proprio comeca no primeiro dia dos filhos
    if (!inicio) {
      inicio = (filhosDe.get(it.id) ?? []).reduce<string | null>((men, f) => {
        const i = inicioDe(f)
        return i && (!men || dia(i) < dia(men)) ? i : men
      }, null)
      if (inicio) inicios.set(it.id, inicio)
    }
    const origem: DatasResolvidas['origem'] = it.dueAt
      ? 'manual'
      : inicio && it.durationDays != null
        ? 'calculado'
        : fim
          ? 'filhos'
          : 'vazio'
    saida.set(it.id, {
      startAt: inicio,
      dueAt: fim,
      origem,
      durationDays: inicio && fim ? diasEntre(inicio, fim) : it.durationDays,
    })
  }
  return saida
}
